import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { CustomerDashboardAccount } from "@ternary/contracts"
import { Button, EmptyState, ThemeRoot, ToneIcon } from "@ternary/ui"
import { type FormEvent, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  addCustomerPaymentMethod,
  addCustomerSubscription,
  bindCustomerMembership,
  CustomerApiError,
  createCustomerPaymentMethodSetup,
  deleteCustomerPaymentMethod,
  joinCustomerDiscord,
  loadCustomerDashboardAccount,
  redeemCustomerReward,
  setDefaultCustomerPaymentMethod,
  unbindCustomerMembership,
  updateCustomerSubscriptionCancellation,
  updateCustomerSubscriptionPlan,
} from "../api.js"
import { browserUuid } from "../browser-id.js"
import { useCardOptions } from "../components/card-options.js"
import { LegacyAccountSidebar } from "../components/legacy-account-shell.js"
import { customerQueryKeys } from "../query-keys.js"
import { NEUTRAL_BRAND } from "../signup/neutral-brand.js"
import { useThemeColor } from "../theme-color.js"

type BoundMember = Extract<CustomerDashboardAccount["member"], { bound: true }>
type DialogName = "invoices" | "payments" | null
type Subscription = BoundMember["billing"]["subscriptions"][number]
type SubscriptionPlan = Subscription["upgrades"][number]
type SubscriptionAction =
  | { subscriptionId: string; type: "cancel" }
  | { subscriptionId: string; type: "reactivate" }
  | { plan: SubscriptionPlan; subscriptionId: string; type: "change_plan" }
  | { plan: BoundMember["billing"]["addOns"][number]; type: "add_on" }

function formatCurrency(value: number, currency: string, language: string) {
  return new Intl.NumberFormat(language, {
    currency,
    style: "currency",
  }).format(value / 100)
}

function formatLegacyPrice(value: number, currency: string, language: string) {
  return new Intl.NumberFormat(language, {
    currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: "currency",
  }).format(value / 100)
}

/** Every dashboard date, as one medium date ("Aug 23, 2026"), on the UTC day it falls on. */
function formatDate(value: string, language: string) {
  return new Intl.DateTimeFormat(language, { dateStyle: "medium", timeZone: "UTC" }).format(
    new Date(value),
  )
}

function formatPaymentMethod(
  payment: BoundMember["billing"]["defaultPaymentMethod"],
  noDefaultCard: string,
) {
  return payment ? `${payment.brand.toUpperCase()} ****${payment.last4}` : noDefaultCard
}

function titleCase(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase())
}

/** The tenant is not known yet: the neutral brand, never Ternary's green, in the colour scheme. */
function DashboardLoading() {
  const { t } = useTranslation()
  return (
    <ThemeRoot brand={NEUTRAL_BRAND}>
      <div
        className="legacy-dashboard-loading"
        aria-busy="true"
        aria-label={t("Loading account")}
        role="status"
      >
        <span className="legacy-dashboard-spinner" />
      </div>
    </ThemeRoot>
  )
}

function WelcomeCard({
  data,
  joining,
  onJoin,
}: {
  data: CustomerDashboardAccount
  joining: boolean
  onJoin: () => void
}) {
  const { t } = useTranslation()
  return (
    <section className="legacy-account-card legacy-welcome-card">
      {data.viewer.avatarUrl ? (
        <img src={data.viewer.avatarUrl} alt="" />
      ) : (
        <span className="legacy-dashboard-empty-avatar" aria-hidden="true" />
      )}
      <h2>
        {t("Welcome")} {data.viewer.displayName}
      </h2>
      {data.member.bound ? (
        <button
          className="legacy-dashboard-primary-action"
          disabled={joining}
          type="button"
          onClick={onJoin}
        >
          {joining ? t("Opening Discord…") : t("Open Discord")}
        </button>
      ) : null}
    </section>
  )
}

function UnboundAccount({ binding, onBind }: { binding: boolean; onBind: (key: string) => void }) {
  const { t } = useTranslation()
  const [key, setKey] = useState("")
  const submit = (event: FormEvent) => {
    event.preventDefault()
    onBind(key)
  }
  return (
    <section className="legacy-account-card" id="key-box">
      <h2>{t("Bind Key")}</h2>
      <p>{t("Enter the key from your purchase email to connect your membership to Discord.")}</p>
      <form onSubmit={submit}>
        <label htmlFor="membership-key">{t("Membership key")}</label>
        <input
          autoComplete="off"
          id="membership-key"
          name="key"
          placeholder="XXXX-XXXX-XXXX"
          required
          type="text"
          value={key}
          onChange={(event) => setKey(event.target.value)}
        />
        <button
          className="legacy-dashboard-primary-action"
          disabled={binding || key.trim().length === 0}
          type="submit"
        >
          {binding ? t("Binding…") : t("Bind")}
        </button>
      </form>
    </section>
  )
}

function BillingCard({
  member,
  onOpenDialog,
}: {
  member: BoundMember
  onOpenDialog: (name: Exclude<DialogName, null>) => void
}) {
  const { t } = useTranslation()
  const payment = member.billing.defaultPaymentMethod
  return (
    <section className="legacy-account-card" id="billing-box">
      <h2>{t("Billing")}</h2>
      <p>
        <strong>{t("Email")}: </strong>
        {member.email}
      </p>
      <p>
        <strong>{t("Invoices")}:</strong>{" "}
        <button
          className="legacy-dashboard-inline-action"
          type="button"
          onClick={() => onOpenDialog("invoices")}
        >
          {t("View Invoices")}
        </button>
      </p>
      <p>
        <strong>{t("Active Card")}: </strong>
        {formatPaymentMethod(payment, t("No default card"))}
      </p>
      <button
        className="legacy-dashboard-primary-action"
        type="button"
        onClick={() => onOpenDialog("payments")}
      >
        {t("Update Payment Info")}
      </button>
    </section>
  )
}

function SubscriptionCard({
  onAction,
  paymentMethod,
  pending,
  subscription,
}: {
  onAction: (action: SubscriptionAction) => void
  paymentMethod: BoundMember["billing"]["defaultPaymentMethod"]
  pending: boolean
  subscription: Subscription
}) {
  const { i18n, t } = useTranslation()
  const language = i18n.resolvedLanguage ?? "en"
  const [selectedPlan, setSelectedPlan] = useState(subscription.upgrades[0]?.planId ?? "")
  const selectedUpgrade = subscription.upgrades.find((upgrade) => upgrade.planId === selectedPlan)
  return (
    <section className="legacy-account-card sub-box">
      <h2>{t("Subscription")}</h2>
      <p>
        <strong>{t("Status")}: </strong>
        <strong
          className={
            subscription.status === "active" || subscription.status === "trialing"
              ? "legacy-status-good"
              : "legacy-status-alert"
          }
        >
          {t(titleCase(subscription.status))}
        </strong>
      </p>
      {subscription.trialEnd ? (
        <p>
          <strong>{t("Trial Ends")}: </strong>
          {formatDate(subscription.trialEnd, language)}
        </p>
      ) : null}
      <p>
        <strong>{t("Renewal Date")}: </strong>
        {formatDate(subscription.currentPeriodEnd, language)}
      </p>
      {subscription.plans.map((plan) => (
        <p key={plan.id}>
          <strong>{t("Plan")}: </strong>
          {plan.name} {formatLegacyPrice(plan.priceMinor, plan.currency, language)}
        </p>
      ))}
      {subscription.cancelAtPeriodEnd ? (
        <button
          className="legacy-dashboard-primary-action"
          disabled={pending}
          type="button"
          onClick={() => onAction({ subscriptionId: subscription.id, type: "reactivate" })}
        >
          {t("Reactivate Subscription")}
        </button>
      ) : subscription.status === "active" || subscription.status === "trialing" ? (
        <button
          className="legacy-dashboard-danger-action"
          disabled={pending}
          type="button"
          onClick={() => onAction({ subscriptionId: subscription.id, type: "cancel" })}
        >
          {t("Cancel Subscription")}
        </button>
      ) : null}
      {subscription.upgrades.length > 0 ? (
        <form
          className="legacy-dashboard-options legacy-subscription-upgrade"
          onSubmit={(event) => {
            event.preventDefault()
            if (selectedUpgrade) {
              onAction({
                plan: selectedUpgrade,
                subscriptionId: subscription.id,
                type: "change_plan",
              })
            }
          }}
        >
          <strong>{t("Update Plan")}</strong>
          <p>
            <strong>{t("Card Used")}: </strong>
            {formatPaymentMethod(paymentMethod, t("No default card"))}
          </p>
          <label className="sr-only" htmlFor={`upgrade-${subscription.id}`}>
            {t("Select a Plan")}
          </label>
          <select
            id={`upgrade-${subscription.id}`}
            value={selectedPlan}
            onChange={(event) => setSelectedPlan(event.target.value)}
          >
            {subscription.upgrades.map((upgrade) => (
              <option key={upgrade.planId} value={upgrade.planId}>
                {upgrade.name} {formatLegacyPrice(upgrade.priceMinor, upgrade.currency, language)}
              </option>
            ))}
          </select>
          <button
            className={
              subscription.cancelAtPeriodEnd
                ? "legacy-dashboard-secondary-action"
                : "legacy-dashboard-primary-action"
            }
            disabled={pending || !selectedUpgrade}
            type="submit"
          >
            {t("Update")}
          </button>
          <small>
            {t(
              "When updating plan, your membership will be prorated.  If you are in the middle of a billing cycle, then you will only owe the difference between your new membership rate minus your current membership rate (e.g. $1200 - $120 = $1,080 total).",
            )}
          </small>
        </form>
      ) : null}
    </section>
  )
}

function SubscriptionCards({
  member,
  onAction,
  pending,
}: {
  member: BoundMember
  onAction: (action: SubscriptionAction) => void
  pending: boolean
}) {
  const { t } = useTranslation()
  if (member.billing.subscriptions.length === 0) {
    return (
      <section className="legacy-account-card sub-box">
        <h2>{t("No Active Subscription")}</h2>
        <p>{t("No subscription is currently attached to this account.")}</p>
      </section>
    )
  }

  return (
    <>
      {member.billing.subscriptions.map((subscription) => (
        <SubscriptionCard
          key={subscription.id}
          onAction={onAction}
          paymentMethod={member.billing.defaultPaymentMethod}
          pending={pending}
          subscription={subscription}
        />
      ))}
    </>
  )
}

function AddOnsCard({
  member,
  onAction,
  pending,
}: {
  member: BoundMember
  onAction: (action: SubscriptionAction) => void
  pending: boolean
}) {
  const { i18n, t } = useTranslation()
  const language = i18n.resolvedLanguage ?? "en"
  const [selectedPlan, setSelectedPlan] = useState(member.billing.addOns[0]?.planId ?? "")
  if (member.billing.addOns.length === 0) {
    return null
  }
  return (
    <section className="legacy-account-card" id="addon-box">
      <h2>{t("Add-On Products")}</h2>
      <p>
        <strong>{t("Card Used")}: </strong>
        {formatPaymentMethod(member.billing.defaultPaymentMethod, t("No default card"))}
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          const plan = member.billing.addOns.find((addOn) => addOn.planId === selectedPlan)
          if (plan) {
            onAction({ plan, type: "add_on" })
          }
        }}
      >
        <label className="sr-only" htmlFor="dashboard-add-on">
          {t("Select a Plan")}
        </label>
        <select
          id="dashboard-add-on"
          value={selectedPlan}
          onChange={(event) => setSelectedPlan(event.target.value)}
        >
          {member.billing.addOns.map((addOn) => (
            <option key={addOn.planId} value={addOn.planId}>
              {addOn.name} {formatLegacyPrice(addOn.priceMinor, addOn.currency, language)}
            </option>
          ))}
        </select>
        <button
          className="legacy-dashboard-primary-action"
          disabled={pending || !selectedPlan}
          type="submit"
        >
          {t("Purchase Add-On")}
        </button>
      </form>
    </section>
  )
}

function ReferralCard({
  member,
  onRedeem,
  pending,
}: {
  member: BoundMember
  onRedeem: (rewardId: string) => void
  pending: boolean
}) {
  const { t } = useTranslation()
  const [rewardId, setRewardId] = useState(member.referrals.rewards[0]?.id ?? "")

  useEffect(() => {
    if (!member.referrals.rewards.some((reward) => reward.id === rewardId)) {
      setRewardId(member.referrals.rewards[0]?.id ?? "")
    }
  }, [member.referrals.rewards, rewardId])

  return (
    <section className="legacy-account-card" id="ref-box">
      <h2>{t("Referral")}</h2>
      <p>
        {t("People Referred")}: <strong>{member.referrals.count}</strong>
      </p>
      <label className="sr-only" htmlFor="referral-code">
        {t("Your referral code")}
      </label>
      <input id="referral-code" readOnly value={member.referrals.code} />
      <form
        id="ref-form"
        onSubmit={(event) => {
          event.preventDefault()
          if (rewardId) {
            onRedeem(rewardId)
          }
        }}
      >
        <label className="sr-only" htmlFor="reward">
          {t("Reward")}
        </label>
        <select
          id="reward"
          disabled={member.referrals.rewards.length === 0}
          value={rewardId}
          onChange={(event) => setRewardId(event.target.value)}
        >
          {member.referrals.rewards.length > 0 ? (
            member.referrals.rewards.map((reward) => (
              <option key={reward.id} value={reward.id}>
                {reward.name} ({t("{{count}} referrals", { count: reward.referralsRequired })})
              </option>
            ))
          ) : (
            <option value="">{t("No Rewards Available")}</option>
          )}
        </select>
        <button
          className="legacy-dashboard-primary-action"
          disabled={pending || !rewardId}
          id="referral-request"
          type="submit"
        >
          {pending ? t("Requesting…") : t("Request Reward")}
        </button>
      </form>
    </section>
  )
}

function KeyCard({ member, onUnbind }: { member: BoundMember; onUnbind: () => void }) {
  const { t } = useTranslation()
  return (
    <section className="legacy-account-card" id="key-box">
      <h2>{t("Key")}</h2>
      <label className="sr-only" htmlFor="connected-membership-key">
        {t("Connected key")}
      </label>
      <input id="connected-membership-key" readOnly value={member.key} />
      <button className="legacy-dashboard-danger-action" type="button" onClick={onUnbind}>
        {t("Unbind Discord")}
      </button>
    </section>
  )
}

function UnbindDialog({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="legacy-dashboard-dialog-backdrop">
      <button
        aria-label={t("Cancel unbind")}
        className="legacy-dashboard-dialog-dismiss"
        type="button"
        onClick={onCancel}
      />
      <section
        aria-labelledby="unbind-dialog-title"
        aria-modal="true"
        className="legacy-dashboard-dialog legacy-dashboard-confirm"
        role="dialog"
      >
        <h2 id="unbind-dialog-title">{t("Unbind Discord?")}</h2>
        <p>
          {t("Unbinding will remove your community access. You can bind the same key again later.")}
        </p>
        <div className="legacy-dashboard-confirm-actions">
          <button
            className="legacy-dashboard-secondary-action"
            disabled={pending}
            type="button"
            onClick={onCancel}
          >
            {t("Cancel")}
          </button>
          <button
            className="legacy-dashboard-danger-action"
            disabled={pending}
            type="button"
            onClick={onConfirm}
          >
            {pending ? t("Unbinding…") : t("Unbind Discord")}
          </button>
        </div>
      </section>
    </div>
  )
}

function SubscriptionActionDialog({
  action,
  error,
  pending,
  onCancel,
  onConfirm,
}: {
  action: SubscriptionAction
  error: string | null
  pending: boolean
  onCancel: () => void
  onConfirm: (input: { coupon: string | null }) => void
}) {
  const { t } = useTranslation()
  const [coupon, setCoupon] = useState("")
  const isCancel = action.type === "cancel"
  const isPlan = action.type === "change_plan" || action.type === "add_on"
  const title =
    action.type === "cancel"
      ? t("Cancel Subscription?")
      : action.type === "reactivate"
        ? t("Reactivate Subscription?")
        : action.type === "change_plan"
          ? t("Update to {{name}}?", { name: action.plan.name })
          : t("Add {{name}}?", { name: action.plan.name })
  return (
    <div className="legacy-dashboard-dialog-backdrop">
      <button
        aria-label={t("Close subscription dialog")}
        className="legacy-dashboard-dialog-dismiss"
        type="button"
        onClick={onCancel}
      />
      <section
        aria-labelledby="subscription-dialog-title"
        aria-modal="true"
        className="legacy-dashboard-dialog legacy-subscription-dialog"
        role="dialog"
      >
        <button
          aria-label={t("Close subscription dialog")}
          className="legacy-dashboard-dialog-close"
          disabled={pending}
          type="button"
          onClick={onCancel}
        >
          ×
        </button>
        <h2 id="subscription-dialog-title">{title}</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onConfirm({
              coupon: isPlan && coupon.trim() ? coupon.trim() : null,
            })
          }}
        >
          {isCancel ? (
            <p>{t("Your access remains active until the end of the current billing period.")}</p>
          ) : null}
          {action.type === "reactivate" ? (
            <p>{t("Your subscription will renew on its existing billing date.")}</p>
          ) : null}
          {isPlan ? (
            <>
              <p>
                {action.type === "change_plan"
                  ? t("The plan change is prorated and billed immediately.")
                  : t("The add-on starts as a separate recurring subscription.")}
              </p>
              <label htmlFor="subscription-coupon">{t("Coupon code (optional)")}</label>
              <input
                id="subscription-coupon"
                maxLength={256}
                value={coupon}
                onChange={(event) => setCoupon(event.target.value)}
              />
            </>
          ) : null}
          {error ? (
            <p className="legacy-payment-error" role="alert">
              {t(error)}
            </p>
          ) : null}
          <div className="legacy-dashboard-confirm-actions">
            <button
              className="legacy-dashboard-secondary-action"
              disabled={pending}
              type="button"
              onClick={onCancel}
            >
              {t("No")}
            </button>
            <button
              className={
                isCancel ? "legacy-dashboard-danger-action" : "legacy-dashboard-primary-action"
              }
              disabled={pending}
              type="submit"
            >
              {pending
                ? t("Updating…")
                : action.type === "cancel"
                  ? t("Cancel Subscription")
                  : action.type === "reactivate"
                    ? t("Reactivate Subscription")
                    : action.type === "change_plan"
                      ? t("Update Plan")
                      : t("Add Plan")}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function StripeNewCard({
  clientSecret,
  pending,
  onConfirm,
  onError,
}: {
  clientSecret: string
  pending: boolean
  onConfirm: (paymentMethodId: string) => void
  onError: (error: Error) => void
}) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [cardFrame, cardOptions] = useCardOptions()

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const card = elements?.getElement(CardElement)
    if (!stripe || !card) {
      onError(new Error(t("Secure card entry is still loading.")))
      return
    }
    const confirmed = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card },
    })
    if (confirmed.error) {
      onError(new Error(confirmed.error.message ?? t("Stripe could not save this card.")))
      return
    }
    const paymentMethod =
      typeof confirmed.setupIntent?.payment_method === "string"
        ? confirmed.setupIntent.payment_method
        : confirmed.setupIntent?.payment_method?.id
    if (!paymentMethod) {
      onError(new Error(t("Stripe did not return a payment method.")))
      return
    }
    onConfirm(paymentMethod)
  }

  return (
    <form className="legacy-payment-new-card" onSubmit={submit}>
      <label htmlFor="dashboard-card-element">{t("Card details")}</label>
      <div className="legacy-payment-stripe-card" id="dashboard-card-element" ref={cardFrame}>
        <CardElement {...(cardOptions ? { options: cardOptions } : {})} />
      </div>
      <button
        className="legacy-dashboard-primary-action"
        disabled={pending || !stripe || !elements}
        type="submit"
      >
        {pending ? t("Saving…") : t("Save new Card")}
      </button>
    </form>
  )
}

function FixtureNewCard({ pending, onConfirm }: { pending: boolean; onConfirm: () => void }) {
  const { t } = useTranslation()
  const [number, setNumber] = useState("4242424242420005")
  const [expiry, setExpiry] = useState("12/30")
  const [cvc, setCvc] = useState("123")
  return (
    <form
      className="legacy-payment-new-card"
      onSubmit={(event) => {
        event.preventDefault()
        onConfirm()
      }}
    >
      <label htmlFor="dashboard-fixture-card-number">{t("Card Number")}</label>
      <input
        id="dashboard-fixture-card-number"
        inputMode="numeric"
        required
        value={number}
        onChange={(event) => setNumber(event.target.value)}
      />
      <div>
        <label htmlFor="dashboard-fixture-card-expiry">
          {t("Expiry")}
          <input
            id="dashboard-fixture-card-expiry"
            required
            value={expiry}
            onChange={(event) => setExpiry(event.target.value)}
          />
        </label>
        <label htmlFor="dashboard-fixture-card-cvc">
          CVC
          <input
            id="dashboard-fixture-card-cvc"
            inputMode="numeric"
            required
            value={cvc}
            onChange={(event) => setCvc(event.target.value)}
          />
        </label>
      </div>
      <button
        className="legacy-dashboard-primary-action"
        disabled={pending || !number || !expiry || !cvc}
        type="submit"
      >
        {pending ? t("Saving…") : t("Save new Card")}
      </button>
    </form>
  )
}

function PaymentMethodsPanel({
  data,
  member,
  onComplete,
  onFailure,
}: {
  data: CustomerDashboardAccount
  member: BoundMember
  onComplete: (message: string) => void
  onFailure: (error: unknown) => void
}) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [selected, setSelected] = useState(
    member.billing.defaultPaymentMethod?.id ?? member.billing.paymentMethods[0]?.id ?? "",
  )
  const fail = (error: unknown) => {
    setFailure(t(error instanceof Error ? error.message : "Could not update this payment method."))
    onFailure(error)
  }
  const setup = useQuery({
    enabled: adding && member.billing.provider === "stripe",
    queryFn: createCustomerPaymentMethodSetup,
    queryKey: customerQueryKeys.paymentMethodSetup,
    retry: false,
  })
  const add = useMutation({
    mutationFn: addCustomerPaymentMethod,
    onError: fail,
    onSuccess: (result) => onComplete(result.message),
  })
  const makeDefault = useMutation({
    mutationFn: setDefaultCustomerPaymentMethod,
    onError: fail,
    onSuccess: (result) => onComplete(result.message),
  })
  const remove = useMutation({
    mutationFn: deleteCustomerPaymentMethod,
    onError: fail,
    onSuccess: (result) => onComplete(result.message),
  })
  const stripePromise = useMemo(
    () =>
      data.mode === "live" && data.organization.publishableKey && data.organization.stripeAccountId
        ? loadStripe(data.organization.publishableKey, {
            stripeAccount: data.organization.stripeAccountId,
          })
        : null,
    [data.mode, data.organization.publishableKey, data.organization.stripeAccountId],
  )

  useEffect(() => {
    setSelected(
      member.billing.defaultPaymentMethod?.id ?? member.billing.paymentMethods[0]?.id ?? "",
    )
  }, [member.billing.defaultPaymentMethod?.id, member.billing.paymentMethods])

  if (adding) {
    return (
      <div className="legacy-payment-add">
        {setup.isFetching ? <p>{t("Preparing secure card entry…")}</p> : null}
        {setup.isError ? (
          <p className="legacy-payment-error">
            {t(
              setup.error instanceof Error ? setup.error.message : "Could not prepare card entry.",
            )}
          </p>
        ) : null}
        {failure ? (
          <p className="legacy-payment-error" role="alert">
            {t(failure)}
          </p>
        ) : null}
        {!setup.isFetching && setup.data?.fixturePaymentMethodId ? (
          <FixtureNewCard
            pending={add.isPending}
            onConfirm={() => add.mutate(setup.data.fixturePaymentMethodId ?? "")}
          />
        ) : !setup.isFetching && setup.data && stripePromise ? (
          <Elements stripe={stripePromise}>
            <StripeNewCard
              clientSecret={setup.data.clientSecret}
              pending={add.isPending}
              onConfirm={(paymentMethodId) => add.mutate(paymentMethodId)}
              onError={fail}
            />
          </Elements>
        ) : !setup.isFetching && setup.data && data.mode === "live" ? (
          <p className="legacy-payment-error">{t("Stripe card entry is not configured.")}</p>
        ) : null}
        <button
          className="legacy-dashboard-secondary-action"
          type="button"
          onClick={() => {
            setAdding(false)
            setFailure(null)
          }}
        >
          {t("Cancel")}
        </button>
      </div>
    )
  }

  return (
    <div className="legacy-payment-existing">
      {member.billing.paymentMethods.length > 0 ? (
        <label htmlFor="dashboard-payment-method">{t("Saved cards")}</label>
      ) : null}
      {member.billing.paymentMethods.length > 0 ? (
        <select
          id="dashboard-payment-method"
          value={selected}
          onChange={(event) => setSelected(event.target.value)}
        >
          {member.billing.paymentMethods.map((paymentMethod) => (
            <option key={paymentMethod.id} value={paymentMethod.id}>
              {paymentMethod.brand.toUpperCase()} ****{paymentMethod.last4}
              {paymentMethod.id === member.billing.defaultPaymentMethod?.id
                ? ` (${t("default")})`
                : ""}
            </option>
          ))}
        </select>
      ) : (
        <p>{t("No cards saved.")}</p>
      )}
      {failure ? (
        <p className="legacy-payment-error" role="alert">
          {t(failure)}
        </p>
      ) : null}
      <div className="legacy-payment-actions">
        <button
          className="legacy-dashboard-secondary-action"
          type="button"
          onClick={() => {
            setFailure(null)
            setAdding(true)
          }}
        >
          {t("Add new")}
        </button>
        <button
          className="legacy-dashboard-danger-action"
          disabled={!selected || remove.isPending}
          type="button"
          onClick={() => {
            setFailure(null)
            remove.mutate(selected)
          }}
        >
          {remove.isPending ? t("Removing…") : t("Remove")}
        </button>
      </div>
      <button
        className="legacy-dashboard-primary-action"
        disabled={!selected || makeDefault.isPending}
        type="button"
        onClick={() => {
          setFailure(null)
          makeDefault.mutate(selected)
        }}
      >
        {makeDefault.isPending ? t("Saving…") : t("Save as Default Card")}
      </button>
    </div>
  )
}

function DashboardDialog({
  data,
  member,
  name,
  onClose,
  onComplete,
  onFailure,
}: {
  data: CustomerDashboardAccount
  member: BoundMember
  name: Exclude<DialogName, null>
  onClose: () => void
  onComplete: (message: string) => void
  onFailure: (error: unknown) => void
}) {
  const { i18n, t } = useTranslation()
  const language = i18n.resolvedLanguage ?? "en"
  return (
    <div className="legacy-dashboard-dialog-backdrop">
      <button
        aria-label={t("Close dialog")}
        className="legacy-dashboard-dialog-dismiss"
        type="button"
        onClick={onClose}
      />
      <section
        aria-labelledby="account-dialog-title"
        aria-modal="true"
        className="legacy-dashboard-dialog"
        role="dialog"
      >
        <button type="button" className="legacy-dashboard-dialog-close" onClick={onClose}>
          <span aria-hidden="true">×</span>
          <span className="sr-only">{t("Close dialog")}</span>
        </button>
        <h2 id="account-dialog-title">
          {name === "payments" ? t("Payment Methods") : t("Invoices")}
        </h2>
        {name === "payments" ? (
          <PaymentMethodsPanel
            data={data}
            member={member}
            onComplete={onComplete}
            onFailure={onFailure}
          />
        ) : (
          <ul>
            {member.billing.invoices.map((invoice) => (
              <li key={invoice.id}>
                <span>
                  {formatDate(invoice.createdAt, language)} ·{" "}
                  {formatCurrency(invoice.totalMinor, invoice.currency, language)}
                </span>
                <a href={invoice.hostedUrl} rel="noreferrer" target="_blank">
                  {t("View")}
                </a>
              </li>
            ))}
          </ul>
        )}
        {name === "invoices" && member.billing.invoices.length === 0 ? (
          <p>{t("Nothing to show yet.")}</p>
        ) : null}
      </section>
    </div>
  )
}

export function DashboardPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [menuOpen, setMenuOpen] = useState(false)
  const [dialog, setDialog] = useState<DialogName>(null)
  const [confirmUnbind, setConfirmUnbind] = useState(false)
  const [subscriptionAction, setSubscriptionAction] = useState<SubscriptionAction | null>(null)
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null)
  const [rewardAttempt, setRewardAttempt] = useState<{
    idempotencyKey: string
    rewardId: string
  } | null>(null)
  const [notice, setNotice] = useState<{ error: boolean; message: string } | null>(null)
  const dashboard = useQuery({
    queryKey: customerQueryKeys.dashboard,
    queryFn: ({ signal }) => loadCustomerDashboardAccount(undefined, { signal }),
  })
  useThemeColor(dashboard.data?.organization.primaryColor)
  const refreshDashboard = () =>
    queryClient.invalidateQueries({ queryKey: customerQueryKeys.dashboard })
  const bindMembership = useMutation({
    mutationFn: bindCustomerMembership,
    onError(error) {
      setNotice({
        error: true,
        message: error instanceof Error ? error.message : "Could not bind this key.",
      })
    },
    async onSuccess(result) {
      setNotice({ error: false, message: result.message })
      await refreshDashboard()
    },
  })
  const joinDiscord = useMutation({
    mutationFn: joinCustomerDiscord,
    onError(error) {
      setNotice({
        error: true,
        message: error instanceof Error ? error.message : "Could not open Discord.",
      })
    },
    onSuccess(result) {
      setNotice({ error: false, message: result.message })
      if (result.serverId) {
        window.open(
          `https://discord.com/channels/${encodeURIComponent(result.serverId)}`,
          "_blank",
          "noopener,noreferrer",
        )
      }
    },
  })
  const unbindMembership = useMutation({
    mutationFn: unbindCustomerMembership,
    onError(error) {
      setNotice({
        error: true,
        message: error instanceof Error ? error.message : "Could not unbind this account.",
      })
    },
    async onSuccess(result) {
      setConfirmUnbind(false)
      setNotice({ error: false, message: result.message })
      await refreshDashboard()
    },
  })
  const updateSubscription = useMutation({
    mutationFn: async (input: { action: SubscriptionAction; coupon: string | null }) => {
      if (input.action.type === "cancel" || input.action.type === "reactivate") {
        return updateCustomerSubscriptionCancellation({
          action: input.action.type,
          subscriptionId: input.action.subscriptionId,
        })
      }
      const idempotencyKey = browserUuid()
      return input.action.type === "change_plan"
        ? updateCustomerSubscriptionPlan({
            coupon: input.coupon,
            idempotencyKey,
            planId: input.action.plan.planId,
            subscriptionId: input.action.subscriptionId,
          })
        : addCustomerSubscription({
            coupon: input.coupon,
            idempotencyKey,
            planId: input.action.plan.planId,
          })
    },
    onError(error) {
      const message = error instanceof Error ? error.message : "Could not update this subscription."
      setSubscriptionError(message)
      setNotice({ error: true, message })
      if (error instanceof CustomerApiError && error.status === 401 && error.authorizationUrl) {
        window.location.assign(error.authorizationUrl)
      }
    },
    async onSuccess(result) {
      setSubscriptionAction(null)
      setSubscriptionError(null)
      setNotice({ error: false, message: result.message })
      await refreshDashboard()
    },
  })
  const redeemReward = useMutation({
    mutationFn: redeemCustomerReward,
    onError(error) {
      setNotice({
        error: true,
        message: error instanceof Error ? error.message : "Could not request this reward.",
      })
    },
    async onSuccess(result) {
      setRewardAttempt(null)
      setNotice({ error: false, message: result.message })
      await refreshDashboard()
    },
  })

  useEffect(() => {
    if (
      dashboard.error instanceof CustomerApiError &&
      dashboard.error.status === 401 &&
      dashboard.error.authorizationUrl
    ) {
      window.location.assign(dashboard.error.authorizationUrl)
    }
  }, [dashboard.error])

  useEffect(() => {
    const error = [
      bindMembership.error,
      joinDiscord.error,
      redeemReward.error,
      unbindMembership.error,
    ].find(
      (candidate) =>
        candidate instanceof CustomerApiError &&
        candidate.status === 401 &&
        candidate.authorizationUrl,
    )
    if (error instanceof CustomerApiError && error.authorizationUrl) {
      window.location.assign(error.authorizationUrl)
    }
  }, [bindMembership.error, joinDiscord.error, redeemReward.error, unbindMembership.error])

  // The one Escape handler for every dashboard dialog. A dialog whose request is pending stays
  // open, as its disabled Cancel button does.
  useEffect(() => {
    if (!dialog && !confirmUnbind && !subscriptionAction) {
      return
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDialog(null)
        if (!unbindMembership.isPending) {
          setConfirmUnbind(false)
        }
        if (!updateSubscription.isPending) {
          setSubscriptionAction(null)
        }
      }
    }
    window.addEventListener("keydown", closeOnEscape)
    return () => window.removeEventListener("keydown", closeOnEscape)
  }, [
    confirmUnbind,
    dialog,
    subscriptionAction,
    unbindMembership.isPending,
    updateSubscription.isPending,
  ])

  if (dashboard.isPending) {
    return <DashboardLoading />
  }

  if (dashboard.isError) {
    const loginRequired =
      dashboard.error instanceof CustomerApiError && dashboard.error.status === 401
    // The tenant is not known yet: the neutral brand, never Ternary's green.
    return (
      <ThemeRoot brand={NEUTRAL_BRAND} className="legacy-dashboard-error">
        <main>
          <h1 className="tny-visually-hidden">{t("Manage Account")}</h1>
          {loginRequired ? (
            <EmptyState
              action={
                <a className="tny-button tny-button-primary" href="/login">
                  {t("Continue with Discord")}
                </a>
              }
              description={t("Your membership is linked to your Discord account.")}
              icon={<ToneIcon tone="info" />}
              title={t("Sign in to open your member dashboard.")}
            />
          ) : (
            <EmptyState
              action={<Button onClick={() => dashboard.refetch()}>{t("Retry dashboard")}</Button>}
              description={t("Check your connection and try again.")}
              icon={<ToneIcon tone="danger" />}
              title={t("Your account could not be loaded.")}
              tone="danger"
            />
          )}
        </main>
      </ThemeRoot>
    )
  }

  const data = dashboard.data
  const member = data.member
  return (
    <ThemeRoot brand={data.organization.primaryColor} className="legacy-dashboard">
      <LegacyAccountSidebar
        data={data}
        menuOpen={menuOpen}
        onToggle={() => setMenuOpen((current) => !current)}
      />
      <main className="legacy-dashboard-main">
        <h1>{t("Manage Account")}</h1>
        {notice ? (
          <div
            className={`legacy-dashboard-notice ${notice.error ? "is-error" : ""}`}
            role={notice.error ? "alert" : "status"}
          >
            <span>{t(notice.message)}</span>
            <button type="button" aria-label={t("Dismiss message")} onClick={() => setNotice(null)}>
              ×
            </button>
          </div>
        ) : null}
        <WelcomeCard
          data={data}
          joining={joinDiscord.isPending}
          onJoin={() => joinDiscord.mutate()}
        />
        {member.bound ? (
          <>
            <BillingCard member={member} onOpenDialog={setDialog} />
            <SubscriptionCards
              member={member}
              pending={updateSubscription.isPending}
              onAction={(action) => {
                setSubscriptionError(null)
                setSubscriptionAction(action)
              }}
            />
            <AddOnsCard
              member={member}
              pending={updateSubscription.isPending}
              onAction={(action) => {
                setSubscriptionError(null)
                setSubscriptionAction(action)
              }}
            />
            <ReferralCard
              member={member}
              pending={redeemReward.isPending}
              onRedeem={(rewardId) => {
                const attempt =
                  rewardAttempt?.rewardId === rewardId
                    ? rewardAttempt
                    : { idempotencyKey: browserUuid(), rewardId }
                setRewardAttempt(attempt)
                redeemReward.mutate(attempt)
              }}
            />
            <KeyCard member={member} onUnbind={() => setConfirmUnbind(true)} />
          </>
        ) : (
          <UnboundAccount
            binding={bindMembership.isPending}
            onBind={(key) => bindMembership.mutate(key)}
          />
        )}
      </main>
      {dialog && member.bound ? (
        <DashboardDialog
          data={data}
          member={member}
          name={dialog}
          onClose={() => setDialog(null)}
          onComplete={(message) => {
            setDialog(null)
            setNotice({ error: false, message })
            void refreshDashboard()
          }}
          onFailure={(error) => {
            setNotice({
              error: true,
              message:
                error instanceof Error ? error.message : "Could not update this payment method.",
            })
            if (
              error instanceof CustomerApiError &&
              error.status === 401 &&
              error.authorizationUrl
            ) {
              window.location.assign(error.authorizationUrl)
            }
          }}
        />
      ) : null}
      {confirmUnbind && member.bound ? (
        <UnbindDialog
          pending={unbindMembership.isPending}
          onCancel={() => setConfirmUnbind(false)}
          onConfirm={() => unbindMembership.mutate()}
        />
      ) : null}
      {subscriptionAction && member.bound ? (
        <SubscriptionActionDialog
          action={subscriptionAction}
          error={subscriptionError}
          pending={updateSubscription.isPending}
          onCancel={() => {
            if (!updateSubscription.isPending) {
              setSubscriptionAction(null)
              setSubscriptionError(null)
            }
          }}
          onConfirm={(input) =>
            updateSubscription.mutate({
              action: subscriptionAction,
              ...input,
            })
          }
        />
      ) : null}
    </ThemeRoot>
  )
}
